/**
 * External dependencies
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useSyncTicketTypes } from '@Hooks/useSyncTicketTypes';
import { useTicketedEvent } from '@Hooks/useTicketedEvent';
import { useQueryClient } from '@tanstack/react-query';
import { TicketedEvent } from '@Types/ticketedEvents';

export interface EditTicketTypesSheetProps extends React.PropsWithChildren {
  event: TicketedEvent;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

// ---- Zod schema & form types ------------------------------------

function sanitizeMoney(value: string) {
  // allow digits and a single dot; clamp to 2 decimals
  let v = value.replace(/[^\d.]/g, '');
  const parts = v.split('.');
  if (parts.length > 2) {
    v = parts[0] + '.' + parts.slice(1).join('');
  }
  const [int, dec] = v.split('.');
  return dec !== undefined ? `${int}.${dec.slice(0, 2)}` : int;
}

// Create a dynamic schema factory that accepts sold counts map
const createFormSchema = (soldCountsMap: Map<string, number>) =>
  z.object({
    ticketTypes: z
      .array(
        z.object({
          id: z.string().optional(),
          name: z.string().min(1, 'Ticket name is required'),
          description: z.string().max(500).optional().or(z.literal('')),
          price: z
            .string()
            .regex(
              /^\d+(\.\d{1,2})?$/,
              'Enter a valid price (e.g., 50 or 50.00)',
            ),
          available: z.string().regex(/^\d+$/, 'Enter a whole number'),
        }),
      )
      .superRefine((ticketTypes, ctx) => {
        ticketTypes.forEach((tt, idx) => {
          if (tt.id) {
            const soldCount = soldCountsMap.get(tt.id) || 0;
            const available = parseInt(tt.available, 10);
            if (!isNaN(available) && available < soldCount) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [idx, 'available'],
                message: `Available quantity cannot be less than ${soldCount} (already sold)`,
              });
            }
          }
        });
      }),
  });

// ----------------------------------------------------------------

export default function EditTicketTypesSheet({
  event,
  isOpen,
  setIsOpen,
}: EditTicketTypesSheetProps) {
  const queryClient = useQueryClient();

  // Fetch event with tickets to count sold tickets per type
  const { data: eventWithTickets } = useTicketedEvent({
    eventId: event.id,
    include: 'tickets',
    enabled: isOpen, // Only fetch when sheet is open
  });

  // Calculate sold count per ticket type
  const soldCountsByTicketTypeId = useMemo(() => {
    const counts = new Map<string, number>();
    if (eventWithTickets?.tickets) {
      eventWithTickets.tickets.forEach((ticket: any) => {
        // Count tickets that have been purchased (have a purchaseId)
        if (ticket.purchaseId && ticket.ticketTypeId) {
          const current = counts.get(ticket.ticketTypeId) || 0;
          counts.set(ticket.ticketTypeId, current + 1);
        }
      });
    }
    return counts;
  }, [eventWithTickets]);

  // Get sold count for a ticket type
  const getSoldCount = (ticketTypeId: string | undefined) => {
    if (!ticketTypeId) return 0;
    return soldCountsByTicketTypeId.get(ticketTypeId) || 0;
  };

  // Convert ticket types to form values
  const defaultTicketTypes = useMemo(() => {
    return (
      event.ticketTypes?.map((tt) => ({
        id: tt.id,
        name: tt.name,
        description: tt.description || '',
        price: String(tt.price),
        available: String(tt.available),
      })) || []
    );
  }, [event.ticketTypes]);

  // Create schema with sold counts
  const formSchema = useMemo(
    () => createFormSchema(soldCountsByTicketTypeId),
    [soldCountsByTicketTypeId],
  );

  type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ticketTypes: defaultTicketTypes,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'ticketTypes',
  });

  const syncMutation = useSyncTicketTypes({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ticketedEvent', event.id],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['ticketed-events'] });
      toastSuccess('Ticket types updated successfully.');
      setIsOpen(false);
    },
    onError: (error) => {
      console.error('Error updating ticket types:', error);
      form.setError('root', {
        type: 'server',
        message: 'We were unable to update the ticket types. Please try again.',
      });
    },
  });

  // Keep form in sync if event changes while sheet is open
  useEffect(() => {
    if (isOpen) {
      form.reset({
        ticketTypes: defaultTicketTypes,
      });
      form.clearErrors('root');
    }
  }, [event, isOpen, form, defaultTicketTypes]);

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      // Reset form when closing
      form.reset({
        ticketTypes: defaultTicketTypes,
      });
      form.clearErrors('root');
    }
  };

  const onSubmit = async (values: FormValues) => {
    // Clear any previous root error
    form.clearErrors('root');

    // Trigger field-level validation (includes superRefine validation)
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    // Convert to API payload format
    const ticketTypes = values.ticketTypes.map((tt, idx) => ({
      id: tt.id, // Include ID for existing ticket types
      name: tt.name.trim(),
      description: tt.description?.trim() || null,
      price: tt.price, // Send as string to preserve precision
      available: parseInt(tt.available, 10),
      sortOrder: idx + 1,
    }));

    await syncMutation.mutateAsync({
      eventId: event.id,
      ticketTypes,
    });
  };

  const handleAdd = () => {
    append({ name: '', description: '', price: '', available: '' });
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{`Edit ticket types for ${event.name}`}</SheetTitle>
          </SheetHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex h-full flex-col"
            >
              <SheetBody className="grow overflow-auto">
                {/* Top-level error banner */}
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  {fields.map((field, idx) => {
                    const ticketTypeId = form.watch(`ticketTypes.${idx}.id`);
                    const soldCount = getSoldCount(ticketTypeId);
                    const hasSoldTickets = soldCount > 0;
                    const currentAvailable = parseInt(
                      form.watch(`ticketTypes.${idx}.available`) || '0',
                      10,
                    );

                    return (
                      <div key={field.id} className="rounded-md border p-4">
                        <div className="mb-4 flex items-center justify-between">
                          <div className="font-semibold">Ticket #{idx + 1}</div>
                          <div className="flex items-center gap-2">
                            {hasSoldTickets && (
                              <span className="text-xs text-zinc-500">
                                {soldCount} sold
                              </span>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => remove(idx)}
                              disabled={fields.length === 1 || hasSoldTickets}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>

                        {/* Name (required) */}
                        <FormField
                          control={form.control}
                          name={`ticketTypes.${idx}.name`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="VIP Sushi Bar Seating"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Description (optional) */}
                        <div className="mt-4">
                          <FormField
                            control={form.control}
                            name={`ticketTypes.${idx}.description`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Textarea
                                    className="resize-y"
                                    placeholder="Enjoy a seat at the sushi bar and see the chef in action."
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <Separator className="my-4" />

                        {/* Price & Available (both required) */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name={`ticketTypes.${idx}.price`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Price</FormLabel>
                                <FormControl>
                                  <Input
                                    inputMode="decimal"
                                    placeholder="50.00"
                                    value={field.value || ''}
                                    onChange={(e) =>
                                      field.onChange(
                                        sanitizeMoney(e.target.value),
                                      )
                                    }
                                    onBlur={field.onBlur}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`ticketTypes.${idx}.available`}
                            render={({ field }) => {
                              const hasError =
                                currentAvailable < soldCount && soldCount > 0;
                              return (
                                <FormItem>
                                  <FormLabel># Available</FormLabel>
                                  <FormControl>
                                    <div className="space-y-1">
                                      <Input
                                        inputMode="numeric"
                                        placeholder="10"
                                        value={field.value || ''}
                                        onChange={(e) =>
                                          field.onChange(
                                            e.target.value.replace(
                                              /[^\d]/g,
                                              '',
                                            ),
                                          )
                                        }
                                        onBlur={field.onBlur}
                                        className={
                                          hasError ? 'border-red-500' : ''
                                        }
                                      />
                                      {soldCount > 0 && (
                                        <p className="text-xs text-zinc-500">
                                          {soldCount} already sold
                                        </p>
                                      )}
                                      {hasError && (
                                        <p className="text-xs text-red-500">
                                          Cannot be less than {soldCount}{' '}
                                          (already sold)
                                        </p>
                                      )}
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              );
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}

                  <Button type="button" variant="secondary" onClick={handleAdd}>
                    + Add another ticket
                  </Button>
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={syncMutation.isPending}
                  disabled={syncMutation.isPending}
                >
                  Update ticket types
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                  disabled={syncMutation.isPending}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
